import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import { MembershipProvider } from '@/components/features/membership/membership-provider';
import { Loader } from '@/components/ui/loader';
import { getUserMembershipsQuery } from '@/queries/getUserMembershipsQuery';
import { Suspense } from 'react';
import { z } from 'zod';
import { AppSidebar } from './_authenticated/-parts/sidebar';
import { invitationSearchSchema } from './invitation';

const dashboardSearchSchema = z
  .object({
    redirect: z.string().optional(),
  })
  .merge(invitationSearchSchema);

export const Route = createFileRoute('/_authenticated')({
  validateSearch: (search) => dashboardSearchSchema.parse(search),
  beforeLoad: async ({ context, search, location }) => {
    const isAuthenticated = context.isAuthenticated;
    if (!isAuthenticated) {
      throw redirect({
        to: '/login',
        // Store the intended destination URL
        search: {
          redirect: location.href,
        },
      });
    }

    if (search.redirect) {
      throw redirect({
        href: search.redirect,
        reloadDocument: true,
      });
    }

    if (search.invitationCode && search.organizationId) {
      throw redirect({
        to: '/organization/join',
        search: {
          organizationId: search.organizationId as string,
          invitationCode: search.invitationCode as string,
        },
      });
    }

    const memberships = await context.queryClient.ensureQueryData(
      getUserMembershipsQuery()
    );

    if (memberships.length <= 0) {
      throw redirect({ to: '/organization/new' });
    }

    return {
      memberships,
      currentOrganizationId: JSON.parse(
        localStorage.getItem('organizationId') as string
      ) as string,
    };
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  return (
    <MembershipProvider>
      {() => {
        return (
          <SidebarProvider>
            <div className="flex h-screen w-full overflow-hidden bg-background">
              <AppSidebar />

              <main className="flex-1 w-0 min-w-0 overflow-y-auto">
                <div className="p-6 md:p-8 h-full">
                  <div className="flex items-center gap-2 mb-4 md:hidden">
                    <SidebarTrigger />
                  </div>
                  <div className="h-full">
                    <Suspense
                      fallback={
                        <div className="w-full h-full flex items-center justify-center">
                          <Loader size="xl" />
                        </div>
                      }
                    >
                      <Outlet />
                    </Suspense>
                  </div>
                </div>
              </main>
            </div>
          </SidebarProvider>
        );
      }}
    </MembershipProvider>
  );
}
